import {SettingParam} from "@/types/node";
import {Button} from "../ui/Button";
import {Plus, Trash} from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {SettingComponentMap} from "../../lib/SettingsBuilder";
import {useState} from "react";

const SettingsModal = ({
  param,
  isOpen,
  isEditing,
  modalSettings,
  values,
  setOpen,
  handleAddItem,
  handleUpdateItem,
  setValues,
  setIsEditing,
}: {
  param: SettingParam;
  isOpen: boolean;
  isEditing: boolean;
  modalSettings: any;
  values: Record<string, any>;
  setOpen: (open: boolean) => void;
  handleAddItem: (value: Record<string, any>) => void;
  handleUpdateItem: (value: Record<string, any>) => void;
  setValues: (values: Record<string, any>) => void;
  setIsEditing: (isEditing: boolean) => void;
}) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        setValues({});
        setOpen(open);
        setIsEditing(!open && isEditing);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full px-3 gap-1.5">
          <Plus className="text-primary-accent w-4 h-4" />
          Add {param.unitName}
        </Button>
      </DialogTrigger>
      <DialogContent className="w-110 min-w-110 block">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit" : "Add"} {param.unitName}
          </DialogTitle>
        </DialogHeader>
        {param.modalDescription && (
          <DialogDescription className="px-5 my-4">
            {param.modalDescription}
          </DialogDescription>
        )}
        <div className="flex items-center space-x-2 flex-col px-5">
          {modalSettings.map((setting: SettingParam, index: number) => {
            const SettingComponent =
              SettingComponentMap[
                setting.type as keyof typeof SettingComponentMap
              ];

            const param = {
              ...setting,
              value: values[setting.key] || "",
            };

            return SettingComponent ? (
              <SettingComponent
                key={setting.key}
                param={param}
                index={index}
                handleChange={(param: any, value: any) =>
                  setValues((prev: Record<string, any>) => ({
                    ...prev,
                    [setting.key]: value,
                  }))
                }
                placeholder={param.placeholder}
              />
            ) : null;
          })}
        </div>
        <DialogFooter className="justify-end">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            disabled={modalSettings.some(
              (setting: any) => !values[setting.key],
            )}
            onClick={() => {
              if (isEditing) {
                handleUpdateItem(values);
              } else {
                handleAddItem(values);
              }
            }}
          >
            {isEditing ? "Update" : "Add"} {param.unitName}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SettingModal = ({
  param,
  handleChange,
  disabled = false,
}: {
  param: SettingParam;
  handleChange: (param: SettingParam, value: SettingParam["value"]) => void;
  disabled: boolean;
}) => {
  const {modalSettings} = param;
  const [values, setValues] = useState<Record<string, any>>({});
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  if (!modalSettings) return null;

  const initialValues = (param.value || []) as Record<string, any>[];

  const handleAddItem = (value: Record<string, any>) => {
    const newValues = [...initialValues, value] as SettingParam["value"];
    handleChange(param, newValues);
    setValues({});
    setOpen(false);
  };

  const handleUpdateItem = (value: Record<string, any>, index: number) => {
    const newValues = initialValues.map((item, i) =>
      i === index ? value : item,
    );
    handleChange(param, newValues);
    setValues({});
    setIsEditing(false);
    setOpen(false);
  };

  const deleteItem = (index: number) => {
    const newValues = initialValues.filter((_, i) => i !== index);
    handleChange(param, newValues);
  };

  return (
    <div
      className={`mb-4 flex flex-col gap-3 ${
        disabled ? "opacity-50 pointer-events-none" : ""
      }`}
    >
      <SettingsModal
        param={param}
        isOpen={open}
        isEditing={isEditing}
        modalSettings={modalSettings}
        values={values}
        setOpen={setOpen}
        handleAddItem={handleAddItem}
        handleUpdateItem={value => handleUpdateItem(value, values.index)}
        setValues={setValues}
        setIsEditing={setIsEditing}
      />
      <div className="flex flex-col gap-3">
        {[...initialValues].reverse().map((value: any, index: number) => {
          const {title, description} = value;
          const reversedIndex = initialValues.length - index - 1;
          return (
            <div
              key={index}
              className="flex flex-col gap-2 shadow-sm p-3 rounded-md border cursor-pointer"
              onClick={() => {
                setIsEditing(true);
                setOpen(true);
                setValues({...value, index: reversedIndex});
              }}
            >
              <div className="text-base text-primary font-semibold flex items-center justify-between">
                {title}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="[&_svg]:size-5 -mr-1 -mt-1"
                  onClick={e => {
                    e.stopPropagation();
                    deleteItem(reversedIndex);
                  }}
                >
                  <Trash className="text-secondary" />
                </Button>
              </div>
              <div className="text-sm text-primary line-clamp-3 overflow-hidden text-ellipsis break-words">
                {description}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
